<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="flex-1">
    <div ref="searchBarRef" class="relative">
      <div class="rounded border border-solid border-blue-400">
        <div class="flex h-8 w-full items-center px-2">
          <button
            class="flex h-6 w-6 basis-6 cursor-pointer items-center justify-center"
            title="search"
            @click="handleSubmit"
          >
            <IconSearch class="h-6 w-6 text-blue-400" />
          </button>
          <input
            ref="searchInputRef"
            v-model.trim="searchInputValue"
            type="search"
            autocomplete="off"
            class="h-5 w-full flex-1 text-sm"
            :placeholder="tGlobal('search-menu.placeholder')"
            @keyup.enter="handleEnter"
          />
          <button
            class="h-4 w-4 basis-4 cursor-pointer"
            title="close"
            @click="handleClose"
          >
            <IconClose class="h-4 w-4 rounded-full bg-gray-300 text-white" />
          </button>
        </div>
      </div>
      <div
        class="absolute left-0 top-10 z-10 w-full rounded bg-white py-2 pl-4 pr-2 shadow-normal shadow-gray-400"
      >
        <div
          class="container-scrollbar max-h-[210px] w-full overflow-y-auto overflow-x-hidden pr-2"
        >
          <div
            v-if="isLoading"
            class="flex h-24 w-full items-center justify-center"
          >
            <LoaderBounce />
          </div>
          <div
            v-else-if="!isShowPopularKeyword && searchResults.length <= 0"
            class="py-2"
          >
            <p class="text-base font-bold text-gray-600">
              {{ tGlobal('search-menu.no-results') }}
            </p>
          </div>
          <ul v-else-if="!isShowPopularKeyword && searchResults.length">
            <li v-for="item in searchResults" :key="item.title" class="py-2">
              <TheNuxtLink
                :to="item.pageUrl"
                :title="item.title"
                class="flex items-center text-base font-bold hover:text-blue-400"
              >
                <span
                  class="inline truncate leading-6"
                  v-html="provideMarkerText(item.title, searchValue)"
                />
                <TheBadge
                  v-if="provideIsShowBadge(item.source)"
                  :name="provideSourceName(item.source)"
                  theme="light-blue"
                  class="ml-2 flex-shrink-0 basis-0 whitespace-nowrap"
                />
              </TheNuxtLink>
            </li>
            <li class="border-t border-solid border-gray-400 pt-2">
              <TheNuxtLink
                :to="`/${locale}/marketplace/search?keyword=${searchValue}&page=1`"
                :title="tGlobal('search-menu.see-all-results')"
                class="text-base font-bold text-blue-400 hover:underline"
              >
                {{ tGlobal('search-menu.see-all-results') }}
              </TheNuxtLink>
            </li>
          </ul>
          <ul v-else>
            <li
              v-for="item in popularKeywords"
              :key="item.searchKey"
              class="py-2"
            >
              <TheNuxtLink
                :to="`/${locale}/marketplace/search?keyword=${item.searchKey}&page=1`"
                :title="item.name"
                class="font-bold hover:text-blue-400"
                data-test="header__search-keyword-link"
                @click="handleClose"
              >
                {{ item.name }}
              </TheNuxtLink>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onClickOutside, refDebounced } from '@vueuse/core';

  import { EnumSource } from '@/assets/types/api/Search';
  import provideIsShowBadge from '@/components/Layout/Search/provideIsShowBadge';

  const router = useRouter();
  const { t, locale } = useI18n({ useScope: 'local' });
  const { t: tGlobal } = useI18n({ useScope: 'global' });
  const { isTablet } = useScreen();
  const searchMenuStore = useSearchMenuStore();
  const { searchValue, searchResults, popularKeywords } =
    storeToRefs(searchMenuStore);

  const searchInputValue = ref<string>(searchValue.value);
  const debounced = refDebounced(searchInputValue, 600);

  const searchBarRef = ref<HTMLElement | null>(null);
  const searchInputRef = ref<HTMLElement | null>(null);

  const { status, execute } = await searchMenuStore.USE_GET_SEARCH();

  const isLoading = computed((): boolean => !!(status.value === 'pending'));

  const isShowPopularKeyword = computed(
    (): boolean => searchValue.value === ''
  );

  const handleSubmit = (): void => {
    if (searchValue.value.trim()) {
      execute();
    }
  };

  const handleEnter = (): void => {
    if (searchValue.value.trim()) {
      router.push(
        `/${locale.value}/marketplace/search?keyword=${searchValue.value}&page=1`
      );
    }
  };

  const handleClose = (): void => {
    searchMenuStore.HANDLE_CLOSE_MENU();
  };

  const provideSourceName = (source: string): string => {
    switch (source) {
      case EnumSource.PRODUCTS:
        return t('products');
      case EnumSource.SOLUTION:
        return t('solutions');
      default:
        return source;
    }
  };

  watch(debounced, (value: string) => {
    searchValue.value = value;
  });

  watch(searchValue, () => handleSubmit());

  onClickOutside(searchBarRef, () => {
    if (!isTablet.value) {
      handleClose();
    }
  });

  onMounted(() => {
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "products": "Products",
    "solutions": "Solutions"
  },
  "en-jp": {
    "products": "Products",
    "solutions": "Solutions"
  },
  "en-tw": {
    "products": "Products",
    "solutions": "Solutions"
  },
  "en-kr": {
    "products": "Products",
    "solutions": "Solutions"
  },
  "en-americas": {
    "products": "Products",
    "solutions": "Solutions"
  },
  "en-asia-pacific": {
    "products": "Products",
    "solutions": "Solutions"
  },
  "en-emea": {
    "products": "Products",
    "solutions": "Solutions"
  },
  "ja-jp": {
    "products": "製品",
    "solutions": "ソリューション"
  },
  "zh-cn": {
    "products": "产品",
    "solutions": "解决方案"
  },
  "zh-tw": {
    "products": "產品",
    "solutions": "解決方案"
  }
}
</i18n>
